import React, { useEffect, useState, useCallback } from "react";
import { Pagination, Form } from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";

import { getDatasetCount, getDatasets } from "../../../services/CkanApiService";
import {
    getBpsDatasetCount,
    getBpsDatasets,
} from "../../../services/BpsApiService";

const ITEMS_PER_PAGE = 50;

const ListDataKumpulanDataCompOpenData = React.memo(() => {
    const { orgName } = useParams();
    const location = useLocation();
    const [datasetCount, setDatasetCount] = useState(0);
    const selectedOrg = location.state?.organisasi || orgName || null;

    // States
    const [items, setItems] = useState([]); // data current page
    const [loading, setLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");

    // Pagination item range
    const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

    /** Format tanggal agar lebih readable */
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        try {
            const date = new Date(dateString);
            if (isNaN(date)) return dateString;

            const months = [
                "Januari",
                "Februari",
                "Maret",
                "April",
                "Mei",
                "Juni",
                "Juli",
                "Agustus",
                "September",
                "Oktober",
                "November",
                "Desember",
            ];

            return `${date.getDate()} ${months[date.getMonth()]
                } ${date.getFullYear()} ${date.getHours()}:${String(
                    date.getMinutes()
                ).padStart(2, "0")}`;
        } catch {
            return dateString;
        }
    };

    /** Fetch data berdasarkan halaman (server-side pagination) */
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // CKAN
            const ckanRes = await getDatasets(
                currentPage,
                ITEMS_PER_PAGE,
                selectedOrg
            );
            const [ckanDataset] = await Promise.all([getDatasetCount()]);
            //console.log("CKAN Total Dataset:", ckanRes.total);
            //console.log("CKAN Dataset di halaman ini:", ckanRes.datasets.length);

            // BPS (opsional: jika ingin digabung)
            const bpsRes = await getBpsDatasets(currentPage, ITEMS_PER_PAGE);
            const [bpsDatasets] = await Promise.all([getBpsDatasetCount()]);
            //console.log("BPS Total Dataset:", bpsDatasets);
            //console.log("BPS Dataset di halaman ini:", bpsRes.datasets.length);

            // Gabungkan hasil (kalau memang datasetnya harus dicampur)
            let combined = [...(ckanRes.datasets || []), ...(bpsRes.datasets || [])];
            let total = (ckanRes.total || 0) + (bpsRes.total || 0);

            // Filter by search query
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                combined = combined.filter((item) => {
                    const title = typeof item.title === "string" ? item.title.toLowerCase() : "";
                    const author = typeof item.author === "string" ? item.author.toLowerCase() : "";
                    const notes = typeof item.notes === "string" ? item.notes.toLowerCase() : "";
                    const subject = typeof item.subject === "string" ? item.subject.toLowerCase() : "";

                    return (
                        title.includes(q) ||
                        author.includes(q) ||
                        notes.includes(q) ||
                        subject.includes(q)
                    );
                });
                total = combined.length;
            }

            setItems(combined);
            setDatasetCount((ckanDataset || 0) + (bpsDatasets || 0));
            setTotalItems(total);
            setTotalPages(Math.ceil(total / ITEMS_PER_PAGE));
        } catch (err) {
            console.error("Error fetch data:", err);
            setItems([]);
            setTotalItems(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, selectedOrg, searchQuery]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    /** Handle search */
    const handleSearch = (query) => {
        setSearchQuery(query);
        setCurrentPage(1); // reset ke halaman pertama
    };

    /** Pagination handler */
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    /** Generate pagination items */
    const createPaginationItems = () => {
        const maxVisible = 5;
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

        const items = [];

        if (start > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (start > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" />);
        }

        for (let i = start; i <= end; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (end < totalPages) {
            if (end < totalPages - 1)
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            items.push(
                <Pagination.Item
                    key={totalPages}
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    return (
        <section className="d-flex align-items-center">
            <div className="container">
                <div className="row content">
                    <div className="col-md-12">
                        <h4 className="m-b-20" data-aos="fade-left">
                            <i className="bx bx-data"></i> Page <b>{currentPage}</b>{" "}
                            Menampilkan{" "}
                            <b>
                                {startItem} - {endItem}
                            </b>{" "}
                            dari <b>{datasetCount}</b> Dataset
                        </h4>
                        <Form.Control
                            type="text"
                            placeholder="Cari dataset..."
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="search-input mb-3"
                        />
                    </div>

                    {loading ? (
                        <div className="col-md-12 text-center">
                            <h4>Loading Data...</h4>
                        </div>
                    ) : items.length > 0 ? (
                        items.map((data, idx) => (
                            <div className="col-md-2" data-aos="fade-in" key={idx}>
                                <Link
                                    to={
                                        data.source === "CKAN"
                                            ? `/open_data/kumpulan_data/${data.name}`
                                            : `/open_data/bps_data/${data.name}`
                                    }
                                    className="box-news"
                                    state={{ data }}
                                >
                                    <div className="box-news-img">
                                        <img src={`${process.env.PUBLIC_URL}/sampang.png`} alt="" />
                                    </div>
                                    <div className="bottom-div">
                                        <i className="bx bx-user-circle"></i> {data.author || "-"}
                                    </div>
                                    <div className="box-content-news">
                                        <h3>{data.title || "-"}</h3>
                                    </div>
                                    <div className="bottom-div">
                                        <i className="bx bx-time"></i>{" "}
                                        {formatDate(data.metadata_modified, data.source)}
                                    </div>
                                </Link>
                            </div>
                        ))
                    ) : (
                        <div className="col-lg-12 box-no-data">
                            {searchQuery
                                ? "Tidak ditemukan dataset yang sesuai dengan pencarian"
                                : "No Data Found"}
                        </div>
                    )}

                    {items.length !== 0 && totalPages > 1 && (
                        <div className="col-md-12 mt-4">
                            <Pagination className="d-flex justify-content-center">
                                <Pagination.First
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                />
                                {createPaginationItems()}
                                <Pagination.Next
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
});

export default ListDataKumpulanDataCompOpenData;
